import { useState } from "react"

/**
 * Change notes for the Budget screen, ported from the prototype's NotePanel / NotesList.
 *
 * The note is an annotation on a change that is already logged — declining to write one
 * still leaves the change recorded, timestamped (MPN-1807). Storage is local for now; the
 * durable log is that ticket's job.
 */
export const NOTE_TAGS = [
  "Performance adjustment",
  "Client request",
  "Margin change",
  "Schedule change",
  "Correction",
  "Other",
]

export type BudgetNote = {
  id: number
  kind?: string
  summary: string
  ts: Date
  text?: string
  tag?: string
}

/** Optional and never blocking: the edit is already applied when this appears. */
export function NotePanel({
  summary,
  onSave,
  onSkip,
}: {
  summary: string
  onSave: (text: string, tag: string) => void
  onSkip: () => void
}) {
  const [text, setText] = useState("")
  const [tag, setTag] = useState(NOTE_TAGS[0])

  return (
    <div className="seg-dialog" onClick={onSkip}>
      <div
        className="seg-dialog__panel seg-dialog__panel--wide"
        role="dialog"
        aria-modal="true"
        aria-label="Add a note about this change?"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="seg-note__title">Add a note about this change?</div>
        <div className="seg-note__summary">{summary}</div>
        <div className="seg-note__row">
          <select className="seg-note__select" value={tag} onChange={(e) => setTag(e.target.value)}>
            {NOTE_TAGS.map((t) => (
              <option key={t}>{t}</option>
            ))}
          </select>
          <input
            className="seg-note__input"
            value={text}
            onChange={(e) => setText(e.target.value)}
            placeholder="e.g. Shifted spend to Meta after CPL review"
          />
          <button type="button" className="seg-btn" onClick={onSkip}>
            Skip
          </button>
          <button type="button" className="seg-btn seg-btn--primary" onClick={() => onSave(text, tag)}>
            Save note
          </button>
        </div>
      </div>
    </div>
  )
}

export function NotesList({
  notes,
  onClose,
  title,
}: {
  notes: BudgetNote[]
  onClose: () => void
  title?: string
}) {
  return (
    <div className="seg-dialog" onClick={onClose}>
      <div
        className="seg-dialog__panel seg-dialog__panel--wide seg-dialog__panel--tall"
        role="dialog"
        aria-modal="true"
        aria-label={title || "Budget change notes"}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="seg-notes__head">
          <span className="seg-dialog__title">{title || "Budget change notes"}</span>
          <span className="seg-notes__count">
            {notes.length} entr{notes.length === 1 ? "y" : "ies"}
          </span>
          <button type="button" className="seg-btn seg-notes__close" onClick={onClose}>
            Close
          </button>
        </div>

        {notes.length === 0 ? (
          <div className="seg-notes__empty">No changes logged yet. Saving an edit records one.</div>
        ) : (
          <div className="seg-notes__list">
            {notes.map((n, i) => (
              <div className="seg-notes__item" key={i}>
                <div className="seg-notes__meta">
                  {n.kind && <span className="seg-notes__kind">{n.kind}</span>}
                  {/* An entry without a tag is a logged change nobody annotated — still a record. */}
                  {n.tag && <span className="seg-notes__tag">{n.tag}</span>}
                  <span className="seg-notes__ts">
                    {n.ts.toLocaleString("en-US", {
                      month: "short",
                      day: "numeric",
                      hour: "numeric",
                      minute: "2-digit",
                    })}
                  </span>
                </div>
                {n.text && <div className="seg-notes__text">{n.text}</div>}
                <div className="seg-notes__summary">{n.summary}</div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  )
}
